import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function DoctorLocations() {
    const [doctorInfo, setDoctorInfo] = useState(null);
    const [schedules, setSchedules] = useState([]);
    const [clinics, setClinics] = useState([]);
    const [selectedClinic, setSelectedClinic] = useState("");
    const [selectedDates, setSelectedDates] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const doc = localStorage.getItem("doctor");
        if (doc) {
            const parsedDoc = JSON.parse(doc);
            setDoctorInfo(parsedDoc);
            
            // Parse existing clinics from doctor profile if available
            if (parsedDoc.clinics) {
                let parsedClinics = [];
                try {
                    parsedClinics = JSON.parse(parsedDoc.clinics);
                } catch(e) {
                    parsedClinics = typeof parsedDoc.clinics === 'string' ? parsedDoc.clinics.split(',').map(c => c.trim()) : [];
                }
                if (Array.isArray(parsedClinics)) {
                    const clinicNames = parsedClinics.map(c => typeof c === 'object' ? c.name : c).filter(Boolean);
                    setClinics(clinicNames);
                    if (clinicNames.length > 0) setSelectedClinic(clinicNames[0]);
                }
            } else if (parsedDoc.clinic_name) {
                setClinics([parsedDoc.clinic_name]);
                setSelectedClinic(parsedDoc.clinic_name);
            }

            fetchSchedules(parsedDoc.id);
        }
    }, []);

    const fetchSchedules = async (id) => {
        try {
            const res = await axios.get(`${API}/api/doctors/${id}/location-schedules`);
            setSchedules(res.data || []);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load location schedules");
        } finally {
            setLoading(false);
        }
    };

    const handleAddSchedule = () => {
        if (!selectedClinic || selectedDates.length === 0) {
            toast.warning("Please select a clinic and at least one date");
            return;
        }

        let newSchedules = [...schedules];
        let addedCount = 0;
        let skippedCount = 0;
        let overwriteCount = 0;

        for (const dateObj of selectedDates) {
            const dateStr = dateObj.toISOString().split("T")[0];
            
            const existingIndex = newSchedules.findIndex(s => s.date === dateStr);
            if (existingIndex >= 0) {
                if (newSchedules[existingIndex].clinic_name === selectedClinic) {
                    skippedCount++;
                } else {
                    if (window.confirm(`Date ${dateStr} is already assigned to ${newSchedules[existingIndex].clinic_name}. Overwrite with ${selectedClinic}?`)) {
                        newSchedules[existingIndex].clinic_name = selectedClinic;
                        overwriteCount++;
                    } else {
                        skippedCount++;
                    }
                }
            } else {
                newSchedules.push({ clinic_name: selectedClinic, date: dateStr });
                addedCount++;
            }
        }

        setSchedules(newSchedules);
        
        let msg = [];
        if (addedCount > 0) msg.push(`Added ${addedCount}`);
        if (overwriteCount > 0) msg.push(`Overwrote ${overwriteCount}`);
        if (skippedCount > 0) msg.push(`Skipped ${skippedCount}`);
        
        if (addedCount > 0 || overwriteCount > 0) {
            toast.success(`Schedule updated: ${msg.join(", ")} locally`);
        } else if (skippedCount > 0) {
            toast.info(`No changes made. Skipped ${skippedCount} dates.`);
        }
        setSelectedDates([]);
    };

    const handleRemoveSchedule = (dateToRemove) => {
        setSchedules(schedules.filter(s => s.date !== dateToRemove));
    };

    const saveSchedules = async () => {
        try {
            await axios.post(`${API}/api/doctors/${doctorInfo.id}/location-schedules`, {
                schedules: schedules
            });
            toast.success("Location schedules saved successfully!");
            fetchSchedules(doctorInfo.id);
        } catch (err) {
            console.error(err);
            toast.error("Failed to save location schedules");
        }
    };

    if (loading) return <div style={{ padding: "24px" }}>Loading schedules...</div>;

    // Group schedules by clinic for display
    const schedulesByClinic = schedules.reduce((acc, curr) => {
        if (!acc[curr.clinic_name]) acc[curr.clinic_name] = [];
        acc[curr.clinic_name].push(curr.date);
        return acc;
    }, {});

    const handleClinicCalendarChange = (clinic, newDatesArray) => {
        const newDatesStrs = newDatesArray.map(d => {
            // Adjust for local time zone to avoid shifting dates backward/forward
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        });
        
        // Filter out old dates for THIS clinic
        const otherSchedules = schedules.filter(s => s.clinic_name !== clinic);
        // Create new schedules for THIS clinic
        const newClinicSchedules = newDatesStrs.map(d => ({ clinic_name: clinic, date: d }));
        
        // Check if any of the new dates are already assigned to a DIFFERENT clinic
        let conflict = false;
        for (const newDate of newDatesStrs) {
            const existing = otherSchedules.find(s => s.date === newDate);
            if (existing) {
                toast.error(`Date ${newDate} is already assigned to ${existing.clinic_name}. Remove it there first.`);
                conflict = true;
                break;
            }
        }
        
        if (!conflict) {
            setSchedules([...otherSchedules, ...newClinicSchedules]);
        }
    };

    return (
        <div style={{ padding: "16px", maxWidth: "1000px", margin: "0 auto", fontFamily: "'Outfit', sans-serif" }}>
            <div style={{ background: "#fff", padding: "24px", borderRadius: "24px", boxShadow: "0 10px 25px rgba(2, 132, 199, 0.1)" }}>
                <h2 style={{ fontSize: "28px", color: "#0c4a6e", marginBottom: "8px", fontWeight: "700" }}>
                    Location Schedules
                </h2>
                <p style={{ color: "#64748b", marginBottom: "32px", fontSize: "16px" }}>
                    Assign specific dates to your clinics. Patients will only be able to book walk-in appointments for a location on the dates you assign here.
                </p>

                <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", marginBottom: "40px", alignItems: "flex-end", background: "#f0f9ff", padding: "24px", borderRadius: "16px", border: "1px solid #e0f2fe" }}>
                    <div style={{ flex: "1", minWidth: "200px" }}>
                        <label style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "#0c4a6e", marginBottom: "8px" }}>
                            Select Clinic
                        </label>
                        {clinics.length > 0 ? (
                            <select 
                                value={selectedClinic} 
                                onChange={(e) => setSelectedClinic(e.target.value)}
                                style={{ width: "100%", padding: "12px", borderRadius: "12px", border: "1px solid #bae6fd", outline: "none", fontSize: "15px" }}
                            >
                                {clinics.map((c, i) => (
                                    <option key={i} value={c}>{c}</option>
                                ))}
                            </select>
                        ) : (
                            <input 
                                type="text" 
                                value={selectedClinic} 
                                onChange={(e) => setSelectedClinic(e.target.value)}
                                placeholder="Enter clinic name"
                                style={{ width: "100%", padding: "12px", borderRadius: "12px", border: "1px solid #bae6fd", outline: "none", fontSize: "15px" }}
                            />
                        )}
                    </div>
                    <div style={{ flex: "1", minWidth: "200px" }}>
                        <label style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "#0c4a6e", marginBottom: "8px" }}>
                            Select Date(s)
                        </label>
                        <DatePicker
                            selectedDates={selectedDates}
                            selectsMultiple
                            onChange={(dates) => setSelectedDates(dates)}
                            minDate={new Date()}
                            placeholderText="Select future date(s)"
                            className="location-datepicker"
                            shouldCloseOnSelect={false}
                        />
                        <style>{`
                            .location-datepicker {
                                width: 100%;
                                padding: 12px;
                                border-radius: 12px;
                                border: 1px solid #bae6fd;
                                outline: none;
                                font-size: 15px;
                                box-sizing: border-box;
                            }
                            .clinic-calendar-container .react-datepicker {
                                border: 1px solid #bae6fd;
                                border-radius: 16px;
                                font-family: 'Outfit', sans-serif;
                            }
                            .clinic-calendar-container .react-datepicker__header {
                                background: #f0f9ff;
                                border-bottom: 1px solid #bae6fd;
                                border-top-left-radius: 16px;
                                border-top-right-radius: 16px;
                            }
                            .clinic-calendar-container .react-datepicker__day--selected,
                            .clinic-calendar-container .react-datepicker__day--keyboard-selected {
                                background-color: #0284c7;
                                color: white;
                                border-radius: 50%;
                            }
                        `}</style>
                    </div>
                    <button 
                        onClick={handleAddSchedule}
                        style={{ padding: "12px 24px", background: "#0284c7", color: "#fff", border: "none", borderRadius: "12px", fontSize: "15px", fontWeight: "600", cursor: "pointer", transition: "0.3s" }}
                        onMouseOver={(e) => e.target.style.background = "#0369a1"}
                        onMouseOut={(e) => e.target.style.background = "#0284c7"}
                    >
                        + Add Dates
                    </button>
                </div>

                <div style={{ marginBottom: "32px" }}>
                    <h3 style={{ fontSize: "20px", color: "#0c4a6e", marginBottom: "16px", fontWeight: "600" }}>Current Schedules</h3>
                    
                    {Object.keys(schedulesByClinic).length === 0 ? (
                        <p style={{ color: "#94a3b8", fontStyle: "italic" }}>No location schedules set for future dates.</p>
                    ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "24px" }}>
                            {Object.keys(schedulesByClinic).map((clinic, idx) => {
                                // Convert date strings to local Date objects safely
                                const clinicDateObjects = schedulesByClinic[clinic].map(dateStr => {
                                    const [y, m, d] = dateStr.split("-").map(Number);
                                    return new Date(y, m - 1, d);
                                });

                                return (
                                    <div key={idx} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "20px", padding: "20px" }}>
                                        <h4 style={{ fontSize: "18px", color: "#0284c7", marginBottom: "16px", borderBottom: "1px solid #e2e8f0", paddingBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <span>🏥 {clinic}</span>
                                            <span style={{ fontSize: "13px", background: "#e0f2fe", color: "#0369a1", padding: "4px 10px", borderRadius: "20px", fontWeight: "600" }}>
                                                {clinicDateObjects.length} dates
                                            </span>
                                        </h4>
                                        
                                        <div className="clinic-calendar-container" style={{ display: "flex", justifyContent: "center" }}>
                                            <DatePicker
                                                inline
                                                selectsMultiple
                                                selectedDates={clinicDateObjects}
                                                onChange={(dates) => handleClinicCalendarChange(clinic, dates)}
                                                minDate={new Date()}
                                            />
                                        </div>
                                        <p style={{ fontSize: "12px", color: "#94a3b8", textAlign: "center", marginTop: "12px" }}>
                                            Click dates on the calendar to add or remove them.
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", borderTop: "1px solid #f1f5f9", paddingTop: "24px" }}>
                    <button 
                        onClick={saveSchedules}
                        style={{ padding: "14px 32px", background: "#10b981", color: "#fff", border: "none", borderRadius: "14px", fontSize: "16px", fontWeight: "700", cursor: "pointer", boxShadow: "0 4px 12px rgba(16, 185, 129, 0.2)", transition: "0.3s" }}
                        onMouseOver={(e) => { e.target.style.transform = "translateY(-2px)"; e.target.style.boxShadow = "0 6px 16px rgba(16, 185, 129, 0.3)"; }}
                        onMouseOut={(e) => { e.target.style.transform = "translateY(0)"; e.target.style.boxShadow = "0 4px 12px rgba(16, 185, 129, 0.2)"; }}
                    >
                        Save All Changes
                    </button>
                </div>
            </div>
        </div>
    );
}
